import { ExternalLink, Unplug } from 'lucide-react';
import { Pill, ReadOnlyValue } from '@/shared/components';
import {
  channelExternalId,
  channelIsActive,
  channelSupportsExternalId,
  channelUpdatedAt,
  type ChannexChannelResource,
  type LastBookingPerOtaItem,
} from '@/shared/api';
import { formatRelative } from '@/shared/lib/format';
import { OtaLogo, getOtaLabel, normalizeChannel } from '../OtaLogo';
import { buildOtaUrl } from './buildOtaUrl';
import { LastBookingCell } from './LastBookingCell';

interface ChannelRowProps {
  channel: ChannexChannelResource;
  lastBooking: LastBookingPerOtaItem | null;
  onDisconnect: () => void;
  disconnecting: boolean;
}

export function ChannelRow({ channel, lastBooking, onDisconnect, disconnecting }: ChannelRowProps) {
  const active = channelIsActive(channel);
  const updatedAt = channelUpdatedAt(channel);
  const normalized = normalizeChannel(channel.attributes.channel);
  const supportsExternalId = channelSupportsExternalId(channel.attributes.channel);
  const externalId = supportsExternalId ? channelExternalId(channel) : null;
  const otaLabel = getOtaLabel(channel.attributes.channel);
  const otaUrl = buildOtaUrl(normalized, externalId);

  return (
    <li className="bg-card border-line/50 shadow-card overflow-hidden rounded-lg border">
      <ChannelHeader
        channel={channel}
        active={active}
        otaLabel={otaLabel}
        otaUrl={otaUrl}
        onDisconnect={onDisconnect}
        disconnecting={disconnecting}
      />
      <dl className="border-line/50 divide-line/40 bg-card-alt/40 grid grid-cols-1 divide-y border-t md:grid-cols-4 md:divide-x md:divide-y-0">
        <div className="px-5 py-3">
          <ReadOnlyValue label="Channex-Channel-ID" value={channel.id} mono copyable={channel.id} />
        </div>
        <div className="px-5 py-3">
          {supportsExternalId ? (
            <ReadOnlyValue
              label={`${otaLabel}-Listing-ID`}
              value={externalId ?? '—'}
              mono
              copyable={externalId ?? undefined}
            />
          ) : (
            <ReadOnlyValue
              label="Listing-Mapping"
              value="Via Channex-Iframe"
              hint={`${otaLabel} nutzt OAuth — keine flache Listing-ID. Mapping läuft im Channex-Iframe.`}
            />
          )}
        </div>
        <div className="px-5 py-3">
          <LastBookingCell lastBooking={lastBooking} />
        </div>
        <div className="px-5 py-3">
          <ReadOnlyValue
            label="Channel-Update"
            value={updatedAt ? formatRelative(updatedAt) : '—'}
            hint={
              updatedAt
                ? `Channel-Config zuletzt geändert: ${new Date(updatedAt).toLocaleString('de-DE')}. Bedeutet NICHT „letzter Booking-Sync".`
                : undefined
            }
          />
        </div>
      </dl>
    </li>
  );
}

interface ChannelHeaderProps {
  channel: ChannexChannelResource;
  active: boolean;
  otaLabel: string;
  otaUrl: string | null;
  onDisconnect: () => void;
  disconnecting: boolean;
}

function ChannelHeader({
  channel,
  active,
  otaLabel,
  otaUrl,
  onDisconnect,
  disconnecting,
}: ChannelHeaderProps) {
  return (
    <div className="flex items-center justify-between gap-4 px-5 py-4">
      <div className="flex min-w-0 items-center gap-3">
        <OtaLogo channel={channel.attributes.channel} active={active} size={36} />
        <div className="min-w-0">
          <div className="text-ink flex items-center gap-2">
            <span className="font-serif text-[17px] font-medium">{otaLabel}</span>
            {active ? (
              <Pill variant="live" size="sm">
                <span className="bg-sage size-1.5 rounded-full" aria-hidden />
                Live
              </Pill>
            ) : (
              <Pill variant="outline" size="sm">
                Pending Mapping
              </Pill>
            )}
          </div>
          {channel.attributes.title && (
            <div className="text-ink-soft mt-0.5 truncate text-[13px]">
              {channel.attributes.title}
            </div>
          )}
        </div>
      </div>
      <div className="flex items-center gap-2">
        {otaUrl && (
          <a
            href={otaUrl}
            target="_blank"
            rel="noopener noreferrer"
            className="border-line/50 text-ink-soft hover:bg-paper/60 hover:text-ink inline-flex items-center gap-1.5 rounded-md border px-3 py-1.5 text-[12px] font-medium transition-colors"
          >
            <ExternalLink size={13} strokeWidth={2} />
            In {otaLabel} öffnen
          </a>
        )}
        <button
          type="button"
          onClick={onDisconnect}
          disabled={disconnecting}
          className="border-sealwax/40 text-sealwax hover:bg-sealwax/10 inline-flex items-center gap-1.5 rounded-md border px-3 py-1.5 text-[12px] font-medium transition-colors disabled:opacity-40"
        >
          <Unplug size={13} strokeWidth={2} />
          {disconnecting ? 'Trenne…' : 'Trennen'}
        </button>
      </div>
    </div>
  );
}
